/**
 * web/src/screens/Notebook/cards.tsx — the feed's card renderers: the typed
 * card (gift / memory / date / note / idea, meeting transcript) and the link
 * card, each with its inline edit and detail forms (card vncVrGk1hc9i: the
 * visible.map body moved out of index.tsx unchanged; all state arrives as
 * props).
 */
import { Pressable } from '../../ui/Pressable';
import { notebookRouteFor, NOTEBOOK_LINK_KINDS } from '../../../../shared/types';
import { titleCase } from '../../lib/titleCase';
import { PhotosStrip } from './PhotosStrip';
import { VisibilityPill } from './VisibilityPill';
import { type NotebookState } from './hooks';
import {
  type Category, type NotebookItem,
  tagText, hostOf, durationLabel, addedLabel, bylineLabel, bylineAuthor, partnerNameOf,
  doneVerb, INTENT_VERB, CATEGORY_LABEL, CATEGORY_TO_KIND, TAG_KIND_LABEL,
  ENTRY_LABEL, MEETING_LABEL, placeFromPick,
} from './types';
import { ENTRY_ICON, MEETING_ICON, ICON_ATTRS, PLACE_PIN } from './icons';

export function NotebookCard({ item, s, giftAck }: { item: NotebookItem; s: NotebookState; giftAck: string }) {
  const {
    tags, suggestions, busyId, busySugg, overriding, overrideText, setOverriding, setOverrideText,
    contextLive, contextId, setContextId, justSavedId, rovingCardId, setFeedRoving,
    foundId,
    editingId, editTitle, editText, editBusy, editError, editTextRef, setEditTitle, setEditText,
    cancelEdit, saveEdit, detailId, detailDate, detailPlace, detailBusy, detailError,
    placeQuery, placeBusy, placeResults, placeNote, setDetailDate, setDetailPlace, setPlaceQuery,
    searchPlaces, closeDetail, saveDetail, openDetail, detailsOpen, suggsOpen, openDetails,
    toggleId, setSuggsOpen, rowError, me, members, canRemove, startEdit, deleteEntry, toggleDone,
    shareEntry, makePrivate, fileKind, retag, resolveSuggestion,
    partnerLabelCap, setPlaceResults, setPlaceNote,
  } = s;
            /* Same canonical field the filter and the toggle use — the card's
               "is-done" state and the Done filter now read one value. */
            const done = item.status === 'done';
            const itemTags = tags.filter(t => t.itemId === item.itemId);
            const itemSuggs = suggestions.filter(s => s.itemId === item.itemId);
            // Notebook v2 typed entry — a plain-text card (gift/memory/date/note/idea).
            if (item.entryType !== 'link') {
              const isMeeting = item.provider === 'meeting';
              const label = isMeeting ? MEETING_LABEL : ENTRY_LABEL[item.entryType];
              const dur = durationLabel(item.durationSeconds);
              return (
                <article
                  className={`nb-card nb-item nb-typed nb-typed-${item.entryType}${isMeeting ? ' nb-typed-meeting' : ''}${contextId === item.itemId ? ' is-open' : ''}${justSavedId === item.itemId ? ' is-new' : ''}${foundId === item.itemId ? ' is-found' : ''}`}
                  key={item.itemId}
                  data-context-card={item.itemId}
                  data-test={isMeeting ? 'meeting-card' : undefined}
                  /* PRD §8 — the feed's one tab stop (roving): focus follows
                     the person as the Arrow keys walk the list. */
                  tabIndex={rovingCardId === item.itemId ? 0 : -1}
                  onFocus={() => setFeedRoving(item.itemId)}
                  /* PRD P1 — "selecting a card opens it in the panel". The card
                     is the target, but its own controls keep priority: a click
                     that started on a link, button or field is theirs, not the
                     panel's. The "Details" button is the keyboard path. */
                  onClick={e => {
                    if (!contextLive) return;
                    const t = e.target as HTMLElement | null;
                    if (t?.closest('a, button, input, select, textarea, label')) return;
                    setContextId(cur => (cur === item.itemId ? null : item.itemId));
                  }}
                >
                  <div className="nb-body">
                    <div className="nb-meta">
                      <span className={`nb-cat nb-cat-${item.entryType}`}>
                        <span className="nb-icon">{isMeeting ? MEETING_ICON : ENTRY_ICON[item.entryType]}</span>
                        {label}
                      </span>
                      {dur && <span className="nb-duration">{dur}</span>}
                      {/* Card wGXKzRwJE57v — "from the thread" only for genuine
                          thread-sourced entries. Every typed card used to carry
                          the tag because quick-adds also set addedBy. */}
                      {item.provider === 'thread' && <span className="nb-provider">from the thread</span>}
                      {item.shared === false && (
                        <span className="nb-private" title="Only you can see this">Just you</span>
                      )}
                      {/* Card xUZFWf8JtHo2 item 4 — the byline below already carries
                          the save date (day + time), so a separate "Added <day>"
                          line was the same date printed twice. The byline is the
                          superset: it keeps WHO saved it AND the exact moment, so
                          the standalone Added span is dropped on typed cards. Link
                          cards keep theirs — they have no byline to supersede it. */}
                      {/* Card SApyMHlbeyhg — WHO saved it, to the minute.
                          Card quWhYyrNiIbt item 4 — the meta row is one line now,
                          so a long name clips with an ellipsis; the untrimmed
                          string rides `title` and is still what the DOM holds. */}
                      <span
                        className="nb-byline"
                        data-test="nb-byline"
                        title={`By ${bylineAuthor(item.addedBy, members, me?.memberId ?? null)}, ${bylineLabel(item.createdAt)}`}
                      >
                        By {bylineAuthor(item.addedBy, members, me?.memberId ?? null)}, {bylineLabel(item.createdAt)}
                      </span>
                    </div>
                    {editingId === item.itemId ? (
                      /* Card 67zOnRgx4YTI item 1 — the saver edits their own
                         words in place instead of adding a second card. */
                      <div className="nb-edit" data-test="nb-edit-form">
                        <label className="nb-label" htmlFor={`nb-edit-${item.itemId}`}>
                          Edit {label.toLowerCase()}
                        </label>
                        <textarea
                          id={`nb-edit-${item.itemId}`}
                          className="pear-chat-input nb-input nb-edit-text"
                          rows={3}
                          ref={editTextRef}
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                        />
                        {editError && (
                          <p className="nb-save-error" data-test="nb-edit-error" role="alert">{editError}</p>
                        )}
                        <div className="nb-composer-foot">
                          {/* Card ggTQe2KbB0A3 MEDIUM-4 — the hint tells the
                              truth about WHO will see the edit: on a 'just
                              me' card the promise used to claim the partner
                              sees it, which is exactly backwards. */}
                          <span className="nb-hint">
                            {item.shared === false
                              ? 'Only you will see this change — the card stays where it is.'
                              : `${partnerLabelCap} sees the change — the card stays where it is.`}
                          </span>
                          <div className="nb-composer-actions">
                            <button type="button" className="pear-btn" data-test="nb-edit-cancel" onClick={cancelEdit}>
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="pear-btn pear-btn-mustard"
                              data-test="nb-edit-save"
                              disabled={editBusy || editText.trim().length === 0}
                              onClick={() => void saveEdit(item)}
                            >
                              {editBusy ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : isMeeting ? (
                      // A transcript runs to thousands of characters — scroll it
                      // instead of letting one card swallow the whole list.
                      <div
                        className="nb-item-title nb-typed-body nb-meeting-body"
                        data-test="meeting-card-body"
                        style={{ maxHeight: 260, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.85rem', fontWeight: 400, lineHeight: 1.5 }}
                      >
                        {item.body ?? ''}
                      </div>
                    ) : (
                      <h3 className="nb-item-title nb-typed-body">{item.body ?? ''}</h3>
                    )}
                    {/* Gift enrichment (card OviOTaNsNivQ): the concierge's
                        acknowledgement the moment the idea is saved, then the
                        detail the search found once it lands. 'done' swaps the
                        ack for the detail — the promise has been kept. */}
                    {item.entryType === 'gift' && item.enrichStatus !== '' && (
                      <div className="gift-enrich" data-test="gift-enrich" data-status={item.enrichStatus}>
                        {item.enrichStatus !== 'done' && (
                          <p className="gift-ack" data-testid="gift-ack">{giftAck}</p>
                        )}
                        {item.enrichStatus === 'pending' && (
                          <p className="gift-wait" data-test="gift-wait">Developing an initial list of suggestions…</p>
                        )}
                        {item.enrichStatus === 'off' && (
                          <p className="gift-state" data-test="gift-off">
                            The concierge is off — turn it on in Settings → Concierge and I'll bring the suggestions.
                          </p>
                        )}
                        {item.enrichStatus === 'none' && (
                          <p className="gift-state" data-test="gift-none">
                            No matching suggestions came back this time — the idea stays on file.
                          </p>
                        )}
                        {item.enrichStatus === 'done' && item.enrichment && (
                          <>
                            <p className="gift-band" data-test="gift-band">
                              {item.enrichment.priceBand || 'Prices not listed'}
                            </p>
                            <p className="gift-why" data-test="gift-why">{item.enrichment.why}</p>
                            {item.enrichment.picks.length > 0 && (
                              <ul className="gift-picks" data-test="gift-picks">
                                {item.enrichment.picks.map(p => (
                                  <li key={p.url}>
                                    <a href={p.url} target="_blank" rel="noreferrer noopener">{p.title}</a>
                                    {p.price && <span className="gift-pick-meta">{p.price}</span>}
                                    {p.rating && <span className="gift-pick-meta">{p.rating}</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                            <p className="gift-source" data-test="gift-source">
                              {item.enrichment.source === 'serpapi' ? 'Google Shopping' : 'Amazon'} ·{' '}
                              {item.enrichment.count} match{item.enrichment.count === 1 ? '' : 'es'}
                            </p>
                          </>
                        )}
                      </div>
                    )}
                    {/* Card 2qBAZOM2LFWa — where and when, once it is known.
                         One line under the words: the day leads, the place
                         follows. Both are optional, so a card with neither
                         renders nothing at all — the detail is earned, not
                         demanded. */}
                    {(item.eventDate != null || item.place) && (
                      <p className="nb-detail-line" data-test="nb-detail-line">
                        {item.eventDate != null && (
                          <span className="nb-detail-when" data-test="nb-detail-when">
                            {new Date(item.eventDate).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}
                          </span>
                        )}
                        {item.place && (
                          /* PRD E1 — the place is an entity chip wherever it
                             renders: same pin, same tint, one vocabulary for
                             "this is a place". The line still carries it, so
                             the day+place sentence stays whole. */
                          <span className="nb-detail-where nb-entity-chip" data-entity="place" data-test="nb-detail-where">
                            {PLACE_PIN}
                            {item.place}
                          </span>
                        )}
                      </p>
                    )}
                    {/* The detail form itself — one card open at a time. */}
                    {detailId === item.itemId && (
                      <div className="nb-detail-form" data-test="nb-detail-form">
                        <label className="nb-label" htmlFor={`nb-detail-date-${item.itemId}`}>
                          What day was it?
                        </label>
                        <input
                          id={`nb-detail-date-${item.itemId}`}
                          type="date"
                          className="pear-chat-input nb-input"
                          data-test="nb-detail-date"
                          value={detailDate}
                          onChange={e => setDetailDate(e.target.value)}
                        />
                        <label className="nb-label" htmlFor={`nb-detail-place-${item.itemId}`}>
                          Where?
                        </label>
                        <div className="nb-place-row">
                          <input
                            id={`nb-detail-place-${item.itemId}`}
                            className="pear-chat-input nb-input"
                            data-test="nb-detail-place"
                            placeholder="Search for a place, or type it in"
                            value={detailPlace}
                            onChange={e => {
                              setDetailPlace(e.target.value);
                              setPlaceQuery(e.target.value);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void searchPlaces();
                              }
                            }}
                          />
                          <Pressable
                            className="pear-btn nb-place-search"
                            data-test="nb-place-search"
                            disabled={placeBusy || !placeQuery.trim()}
                            onClick={() => void searchPlaces()}
                          >
                            {placeBusy ? 'Searching…' : 'Search'}
                          </Pressable>
                        </div>
                        {placeNote && <p className="nb-hint" data-test="nb-place-note">{placeNote}</p>}
                        {placeResults.length > 0 && (
                          <ul className="nb-place-results" data-test="nb-place-results">
                            {placeResults.map(p => (
                              <li key={p.id}>
                                <button
                                  type="button"
                                  className="nb-place-result"
                                  data-test="nb-place-result"
                                  onClick={() => {
                                    setDetailPlace(placeFromPick(p));
                                    setPlaceResults([]);
                                    setPlaceNote(null);
                                  }}
                                >
                                  <span className="nb-place-name">{p.name}</span>
                                  {p.address && <span className="nb-place-addr">{p.address}</span>}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {detailError && (
                          <p className="nb-save-error" data-test="nb-detail-error" role="alert">{detailError}</p>
                        )}
                        <div className="nb-composer-actions">
                          <Pressable className="pear-btn" data-test="nb-detail-cancel" onClick={closeDetail}>
                            Cancel
                          </Pressable>
                          <Pressable
                            className="pear-btn pear-btn-mustard"
                            data-test="nb-detail-save"
                            disabled={detailBusy}
                            onClick={() => void saveDetail(item)}
                          >
                            {detailBusy ? 'Saving…' : 'Save'}
                          </Pressable>
                        </div>
                      </div>
                    )}
                    {/* Photos (epic lARMIX56PSIY) — the strip sits under the
                        words, on the memory card only. Links and gifts have no
                        photos: they are references, not moments. */}
                    {item.entryType === 'memory' && <PhotosStrip memoryId={item.itemId} />}
                    <div className="nb-actions">
                      {/* Card 2wn9seit8zGi (PRD K4/P1) — the desktop affordance
                          that opens this card in the context panel. Hidden
                          below 760px, where there is no panel to open (PRD
                          L5), so it never renders as a dead control. */}
                      {contextLive && (
                        <Pressable
                          className="nb-open-panel"
                          aria-label={`Open ${label} in the details panel`}
                          data-test="nb-open-panel"
                          onClick={() => setContextId(item.itemId)}
                        >
                          View
                        </Pressable>
                      )}
                      {/* Card 67zOnRgx4YTI item 1 — Edit, owner only, same
                          gate as Remove: the words can be rewritten in place. */}
                      {canRemove(item) && editingId !== item.itemId && (
                        <Pressable
                          className="nb-edit-btn"
                          aria-label={`Edit ${label}`}
                          data-test="nb-edit"
                          disabled={editBusy}
                          onClick={() => startEdit(item)}
                        >
                          Edit
                        </Pressable>
                      )}
                      {/* Card 2qBAZOM2LFWa — the detail pill. Small, owner-only
                          (it writes, so it sits on the same gate as Edit), and
                          it says what it holds once something is held: a card
                          with a day reads "14 Oct", so the pill is the card's
                          own summary and not a mystery control. */}
                      {canRemove(item) && (
                        <Pressable
                          className={`nb-detail-btn${(item.eventDate != null || item.place) ? ' has-detail' : ''}`}
                          aria-label={`Edit the detail on this ${label.toLowerCase()}`}
                          aria-expanded={detailId === item.itemId}
                          data-test="nb-detail"
                          disabled={detailBusy}
                          onClick={() => (detailId === item.itemId ? closeDetail() : openDetail(item))}
                        >
                          {item.eventDate != null
                            ? new Date(item.eventDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })
                            : item.place
                              ? item.place
                              : 'Detail'}
                        </Pressable>
                      )}
                      {/* Visibility pill (card quWhYyrNiIbt, PRD K3) — one
                          control that reads the card's audience and flips it
                          from a small menu. Replaces the old share/make-private
                          pair; owner-only, same confirm + PUT behind it. */}
                      {canRemove(item) || item.shared !== false ? (
                        <VisibilityPill
                          shared={item.shared !== false}
                          mine={canRemove(item)}
                          partnerName={partnerNameOf(members, me?.memberId ?? null)}
                          busy={busyId === item.itemId}
                          onSet={next => void (next ? shareEntry(item) : makePrivate(item))}
                        />
                      ) : null}
                      {canRemove(item) && (
                        <Pressable
                          className="nb-delete"
                          aria-label={`Delete ${label}`}
                          onClick={() => void deleteEntry(item)}
                        >
                          Remove
                        </Pressable>
                      )}
                    </div>
                  </div>
                </article>
              );
            }
            return (
              /* Sol review 2026-10-19, card xNGOA5rZz9LV phase 2 — the calm
                 card face. A default visit now reads: thumbnail, title, one
                 source line, the private badge, and Open / Done. Everything
                 that classifies or explains the card (kind verdict, home,
                 subtype, added date, description, rationale, tags, filing
                 picker, Remove) sits behind one Details disclosure, so the
                 list scans instead of audits. Suggestions keep a count-bearing
                 opener on the face — a pending scan still announces itself. */
              <article
                className={`nb-card nb-item${done ? ' is-done' : ''}${contextId === item.itemId ? ' is-open' : ''}${justSavedId === item.itemId ? ' is-new' : ''}${foundId === item.itemId ? ' is-found' : ''}`}
                key={item.itemId}
                data-context-card={item.itemId}
                /* Same selection contract as the typed cards above, plus the
                   PRD §8 roving tab stop the Arrow keys move. */
                tabIndex={rovingCardId === item.itemId ? 0 : -1}
                onFocus={() => setFeedRoving(item.itemId)}
                onClick={e => {
                  if (!contextLive) return;
                  const t = e.target as HTMLElement | null;
                  if (t?.closest('a, button, input, select, textarea, label')) return;
                  setContextId(cur => (cur === item.itemId ? null : item.itemId));
                }}
              >
                {item.imageUrl && (
                  <img className="nb-art" src={item.imageUrl} alt="" loading="lazy" />
                )}
                <div className="nb-body">
                  {editingId === item.itemId ? (
                    /* Card 67zOnRgx4YTI item 1 — link cards edit both the name
                       and the words around it, on the face where they read. */
                    <div className="nb-edit" data-test="nb-edit-form">
                      <label className="nb-label" htmlFor={`nb-edit-title-${item.itemId}`}>Title</label>
                      <input
                        id={`nb-edit-title-${item.itemId}`}
                        className="pear-chat-input nb-input"
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                      />
                      <label className="nb-label" htmlFor={`nb-edit-${item.itemId}`}>Description</label>
                      <textarea
                        id={`nb-edit-${item.itemId}`}
                        className="pear-chat-input nb-input nb-edit-text"
                        rows={3}
                        ref={editTextRef}
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                      />
                      {editError && (
                        <p className="nb-save-error" data-test="nb-edit-error" role="alert">{editError}</p>
                      )}
                      <div className="nb-composer-foot">
                        {/* Card ggTQe2KbB0A3 MEDIUM-4 — same truth on link
                            cards: a 'just me' link edits privately. */}
                        <span className="nb-hint">
                          {item.shared === false
                            ? 'Only you will see this change — the link stays where it is.'
                            : `${partnerLabelCap} sees the change — the link stays where it is.`}
                        </span>
                        <div className="nb-composer-actions">
                          <button type="button" className="pear-btn" data-test="nb-edit-cancel" onClick={cancelEdit}>
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="pear-btn pear-btn-mustard"
                            data-test="nb-edit-save"
                            disabled={editBusy || editTitle.trim().length === 0}
                            onClick={() => void saveEdit(item)}
                          >
                            {editBusy ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <h3 className="nb-item-title">{item.title ?? hostOf(item.url)}</h3>
                  )}
                  {/* The one source line: where it came from and how long it
                      runs. The provider name leads when we know it; an
                      unknown provider falls back to the host. */}
                  <p className="nb-url">
                    {item.provider === 'unknown' ? hostOf(item.url) : item.provider}
                    {durationLabel(item.durationSeconds) ? ` · ${durationLabel(item.durationSeconds)}` : ''}
                  </p>
                  {/* PRD K1/E1 — the entity chip on a link card's face: the
                      place the row already holds, so a link that names a
                      place says so without opening it. A card with no place
                      renders nothing here — the text stays as it was. */}
                  {item.place && (
                    <div className="nb-entity-chips" data-test="nb-entity-chips">
                      <span className="nb-entity-chip" data-entity="place" data-test="nb-entity-chip">
                        {PLACE_PIN}
                        {item.place}
                      </span>
                    </div>
                  )}
                  {/* The theme, in plain words (Jonathan 2026-10-20): the card's
                      one line of value, read through the editorial label so the
                      stored key stays canonical and searchable. A theme the scan
                      proposed reads as the card's own sentence rather than as a
                      chore to accept — accepting it is still what files it. */}
                  {(() => {
                    const accepted = itemTags.find(t => t.kind === 'theme');
                    const proposed = itemSuggs.find(s => s.kind === 'theme');
                    const theme = accepted ?? proposed;
                    if (!theme) return null;
                    return (
                      <p
                        className="nb-theme"
                        data-source={accepted ? 'agent' : 'suggested'}
                        data-theme={theme.tag}
                      >
                        {tagText('theme', theme.tag)}
                      </p>
                    );
                  })()}
                  {/* A link that is not shared got that way on purpose (the gift
                      toggle, card PQX46vNPdbwn) — the badge stays on the face,
                      because who can see a card is the one fact worth keeping
                      visible. */}
                  {!item.shared && (
                    <div className="nb-meta">
                      <span className="nb-private" title="Only you can see this">Just you</span>
                    </div>
                  )}

                  {/* The chooser (card xNGOA5rZz9LV phase 3, sol review
                      2026-10-19 high): the cascade's 'ask' band persisted its
                      ranked candidates but nothing on screen ever consumed
                      them, so an unresolved link kept a filed-looking card and
                      the only correction on offer spoke the legacy media
                      categories — which cannot reach Events, Places, Things or
                      Trips. The options are now buttons on the face (best
                      guess first) and "Somewhere else" opens the full list.
                      Answering clears the band server-side, so the chooser
                      leaves with the question it answered. */}
                  {item.kindTier === 'ask' && (
                    <div className="nb-chooser" data-test="nb-chooser">
                      <p className="nb-chooser-head" data-test="nb-chooser-head">
                        Not sure where this files — which is it?
                      </p>
                      {(item.kindOptions ?? []).length > 0 && (
                        <div className="nb-chooser-opts">
                          {(item.kindOptions ?? []).map((o, i) => {
                            const label = `${notebookRouteFor(o.kind).kindLabel}${o.subtype ? ` › ${titleCase(o.subtype)}` : ''}`;
                            return (
                              <button
                                type="button"
                                key={`${o.kind}-${o.subtype ?? ''}`}
                                data-test={`nb-chooser-opt-${i}`}
                                className="nb-chooser-opt"
                                disabled={busyId === item.itemId}
                                onClick={() => void fileKind(item, o.kind, o.subtype, label)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <div className="nb-chooser-more">
                        <label className="nb-chooser-more-label" htmlFor={`nb-chooser-kind-${item.itemId}`}>
                          Somewhere else
                        </label>
                        <select
                          id={`nb-chooser-kind-${item.itemId}`}
                          className="nb-chooser-kind"
                          data-test="nb-chooser-kind"
                          aria-label="File this link under another kind"
                          value=""
                          disabled={busyId === item.itemId}
                          onChange={e => {
                            const k = e.target.value;
                            if (k) void fileKind(item, k, null, notebookRouteFor(k).kindLabel);
                          }}
                        >
                          <option value="" disabled>Choose a kind…</option>
                          {NOTEBOOK_LINK_KINDS.map(k => (
                            <option key={k} value={k}>{notebookRouteFor(k).kindLabel}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* A failure on THIS card, said on this card (phase 3). */}
                  {rowError?.itemId === item.itemId && (
                    <p className="nb-save-error nb-row-error" data-test="nb-card-error" role="alert">{rowError.message}</p>
                  )}

                  <div className="nb-actions">
                    <a className="nb-intent" href={item.url} target="_blank" rel="noreferrer noopener">
                      {INTENT_VERB[item.intent]} ↗
                    </a>
                    {/* Card 2wn9seit8zGi — same desktop affordance the typed
                        cards carry: open this card in the context panel. */}
                    {contextLive && (
                      <Pressable
                        className="nb-open-panel"
                        aria-label={`Open ${item.title?.trim() || hostOf(item.url)} in the details panel`}
                        data-test="nb-open-panel"
                        onClick={() => setContextId(item.itemId)}
                      >
                        View
                      </Pressable>
                    )}
                    <Pressable
                      className={`nb-done${done ? ' is-on' : ''}`}
                      disabled={busyId === item.itemId}
                      onClick={() => void toggleDone(item)}
                      aria-pressed={done}
                      data-test="nb-done"
                    >
                      {/* One verb, said once (Jonathan 2026-10-20): the CTA
                          beside this already reads "Read ↗", so the done
                          control must not say "Mark read" next to it. The
                          context-aware verb survives wherever it adds
                          information (Watch / Listen) and collapses to Done
                          where it would only repeat the CTA. */}
                      {(() => {
                        const verb = doneVerb(item.kind);
                        const repeatsCta = verb.toLowerCase() === INTENT_VERB[item.intent].toLowerCase();
                        if (repeatsCta) return done ? '✓ Done' : 'Mark done';
                        return done ? `✓ ${verb}` : `Mark ${verb.toLowerCase()}`;
                      })()}
                    </Pressable>
                    {/* A pending scan still has to be reachable from the face,
                        but as a count rather than a wall of editors. */}
                    {itemSuggs.length > 0 && (
                      <button
                        type="button"
                        className="nb-suggs-toggle"
                        data-test="nb-suggs-toggle"
                        aria-expanded={suggsOpen.has(item.itemId)}
                        onClick={() => setSuggsOpen(s => toggleId(s, item.itemId))}
                      >
                        {itemSuggs.length} suggested tag{itemSuggs.length === 1 ? '' : 's'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="nb-details-btn"
                      data-test="nb-details-toggle"
                      aria-expanded={detailsOpen.has(item.itemId)}
                      onClick={() => openDetails(item.itemId)}
                    >
                      Details
                    </button>
                    {/* Card 67zOnRgx4YTI item 1 — Edit, owner only: the name
                        and the words around it can be rewritten in place. */}
                    {canRemove(item) && editingId !== item.itemId && (
                      <button
                        type="button"
                        className="nb-edit-btn"
                        data-test="nb-edit"
                        disabled={editBusy}
                        onClick={() => startEdit(item)}
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {itemSuggs.length > 0 && suggsOpen.has(item.itemId) && (
                    <div className="nb-suggs" data-test="nb-suggs">
                      <p className="nb-suggs-head">Suggested — nothing is tagged until you say so</p>
                      {itemSuggs.map(s => (
                        <div className="nb-sugg" key={s.id}>
                          <p className="nb-sugg-why">
                            <span className="nb-sugg-tag">{TAG_KIND_LABEL[s.kind]} · {tagText(s.kind, s.tag)}</span>
                            {s.subject && (
                              <span className="nb-sugg-subject" data-subject={s.subject}>
                                about {s.subject}
                              </span>
                            )}
                            {s.rationale}
                          </p>
                          <div className="nb-sugg-actions">
                            <Pressable
                              className="nb-sugg-use"
                              disabled={busySugg === s.id}
                              onClick={() => void resolveSuggestion(s.id, 'accept')}
                            >
                              {busySugg === s.id ? 'Saving…' : 'Use this tag'}
                            </Pressable>
                            <Pressable
                              className="nb-sugg-mine"
                              disabled={busySugg === s.id}
                              aria-expanded={overriding === s.id}
                              onClick={() => {
                                setOverriding(overriding === s.id ? null : s.id);
                                setOverrideText('');
                              }}
                            >
                              My own word
                            </Pressable>
                          </div>
                          {overriding === s.id && (
                            <form
                              className="nb-sugg-form"
                              onSubmit={e => {
                                e.preventDefault();
                                const v = overrideText.trim();
                                if (v) void resolveSuggestion(s.id, 'override', v);
                              }}
                            >
                              <input
                                className="pear-chat-input nb-sugg-input"
                                value={overrideText}
                                maxLength={40}
                                placeholder="Your word"
                                aria-label={`Your ${TAG_KIND_LABEL[s.kind].toLowerCase()} tag`}
                                onChange={e => setOverrideText(e.target.value)}
                              />
                              <button
                                type="submit"
                                className="pear-btn pear-btn-mustard nb-sugg-save"
                                disabled={overrideText.trim().length === 0}
                              >
                                Save
                              </button>
                            </form>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Everything that explains or refiles the card. Hidden by
                      default, never unmounted — the date line and the filing
                      picker are still on every card, one tap away. */}
                  <div
                    className={`nb-details${detailsOpen.has(item.itemId) ? ' is-open' : ''}`}
                    data-test="nb-details"
                  >
                    <div className="nb-meta">
                      {/* One verdict per card (founder email 2026-10-19): the kind
                          filing IS the classification — showing the legacy
                          media-category chip beside it left cards that read
                          "Something else" while filing to Reading. The kind
                          label leads; the legacy category only shows when no
                          kind verdict exists yet (kind 'other'). */}
                      {/* Card FsDDh1AF6Ege — when it was saved, on every card. */}
                      <span className="nb-added" data-test="nb-date-added">Added {addedLabel(item.createdAt)}</span>
                      {/* Where the card filed (card 4CXp7HfSIAGd): named only when
                          a route moved it off the catch-all kind, so an ordinary
                          paste looks exactly as before. */}
                      {item.kind && item.kind !== 'other' && (
                        <span className="nb-home" data-home={item.kind}>{notebookRouteFor(item.kind).homeLabel}</span>
                      )}
                      {/* The subtype tab the card answers to (PRD §5 layer 2). */}
                      {item.subtype && (
                        <span className="nb-subtype-label" data-subtype={item.subtype}>{titleCase(item.subtype)}</span>
                      )}
                    </div>
                    {/* The ".nb-concierge" promise row is gone (sol review
                        2026-10-19, card xNGOA5rZz9LV): it promised a curated
                        recommendations pass the product does not run. */}
                    {item.description && <p className="nb-desc">{item.description}</p>}

                    {/* The host-proof sentence is provenance, so it yields to
                        the theme line when one exists (Jonathan 2026-10-20:
                        "instead of just saying how it arrived at the category,
                        give a brief indication of theme"). It stays for cards
                        with no theme, and always in the refile path. */}
                    {item.categoryReason && !itemTags.some(t => t.kind === 'theme') && !itemSuggs.some(s => s.kind === 'theme') && (
                      <p className="nb-reason" title="Why this card was categorised this way">
                        {item.categoryReason}
                        {item.categorizedBy === null && item.category !== 'other' ? ' · suggested' : ' · yours'}
                      </p>
                    )}

                    {itemTags.length > 0 && (
                      <div className="nb-tags">
                        {itemTags.map(t => (
                          <span className="nb-tag" key={t.id} data-source={t.source}>
                            {TAG_KIND_LABEL[t.kind]} · {tagText(t.kind, t.tag)}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="nb-details-actions">
                      <select
                        className="nb-retag"
                        aria-label="Change filing"
                        value={item.category}
                        disabled={busyId === item.itemId}
                        onChange={e => void retag(item, e.target.value as Category)}
                      >
                        {(Object.keys(CATEGORY_LABEL) as Category[]).map(c => (
                          <option key={c} value={c}>
                            {/* One vocabulary (Jonathan 2026-10-20): the option
                                names the shelf once. "Article — Read" repeated
                                the same verdict in two taxonomies. */}
                            {CATEGORY_TO_KIND[c] !== 'other' ? notebookRouteFor(CATEGORY_TO_KIND[c]).kindLabel : CATEGORY_LABEL[c]}
                          </option>
                        ))}
                      </select>
                      {/* Visibility pill (card quWhYyrNiIbt, PRD K3) — links get
                          the same single audience control typed entries carry.
                          Owner-only, same confirm + PUT behind it. */}
                      {canRemove(item) || !!item.shared ? (
                        <VisibilityPill
                          shared={!!item.shared}
                          mine={canRemove(item)}
                          partnerName={partnerNameOf(members, me?.memberId ?? null)}
                          busy={busyId === item.itemId}
                          onSet={next => void (next ? shareEntry(item) : makePrivate(item))}
                        />
                      ) : null}
                      {/* Card FsDDh1AF6Ege — links get the same destructive row the
                          typed cards carry. Same confirm, same PUT removed:true.
                          Card Q4BluQX8fXgj — only the creator sees Remove. */}
                      {canRemove(item) && (
                        <Pressable
                          className="nb-delete"
                          aria-label={`Delete ${CATEGORY_LABEL[item.category]} card`}
                          disabled={busyId === item.itemId}
                          onClick={() => void deleteEntry(item)}
                        >
                          Remove
                        </Pressable>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
}
